'use client';

import { useState, useEffect } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import {
  Ticket,
  Eye,
  CheckCircle,
  Loader2,
  AlertCircle,
} from 'lucide-react';
import { aiAssistantApi, type AITicket, type TicketStatus } from '@/lib/api/ai-assistant';

const STATUS_COLORS: Record<TicketStatus, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  IN_PROGRESS: 'bg-blue-100 text-blue-700',
  RESOLVED: 'bg-green-100 text-green-700',
  CLOSED: 'bg-gray-100 text-gray-700',
};

const PRIORITY_COLORS = {
  LOW: 'text-gray-500',
  MEDIUM: 'text-blue-500',
  HIGH: 'text-orange-500',
  URGENT: 'text-red-500',
};

/**
 * AI 助手工单管理页面
 */
export default function TicketsPage() {
  const { t } = useTranslation();
  const [tickets, setTickets] = useState<AITicket[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<TicketStatus | 'ALL'>('ALL');
  const [selectedTicket, setSelectedTicket] = useState<AITicket | null>(null);
  const [resolution, setResolution] = useState('');
  const [isResolving, setIsResolving] = useState(false);

  useEffect(() => {
    loadTickets();
  }, [filter]);

  const loadTickets = async () => {
    setIsLoading(true);
    try {
      const params: any = { page: 1, limit: 50 };
      if (filter !== 'ALL') {
        params.status = filter;
      }
      const response: any = await aiAssistantApi.getTickets(params);
      setTickets(response.data.items || []);
    } catch (err) {
      console.error('Failed to load tickets:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResolve = async () => {
    if (!selectedTicket || !resolution.trim()) return;

    setIsResolving(true);
    try {
      await aiAssistantApi.resolveTicket(selectedTicket.id, { resolution });
      setSelectedTicket(null);
      setResolution('');
      loadTickets();
    } catch (err) {
      console.error('Failed to resolve ticket:', err);
    } finally {
      setIsResolving(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="p-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {'Ticket Management'}
          </h1>
          <p className="text-gray-500 mt-1">
            Manage escalated tickets from AI assistant
          </p>
        </div>
      </div>

      {/* 过滤器 */}
      <div className="flex gap-2 mb-6">
        {(['ALL', 'PENDING', 'IN_PROGRESS', 'RESOLVED', 'CLOSED'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === status
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-600 border hover:bg-gray-50'
            }`}
          >
            {status === 'ALL'
              ? 'All'
              : t.aiAssistant?.tickets?.statuses?.[status as TicketStatus] || status}
          </button>
        ))}
      </div>

      {/* 工单列表 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : tickets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-gray-500">
            <Ticket className="w-12 h-12 mb-4 text-gray-300" />
            <p>No tickets found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {'ID'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {'Category'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {'Priority'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {'Status'}
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {'Created'}
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {'Actions'}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {tickets.map((ticket) => (
                <tr key={ticket.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm text-gray-900">
                      {ticket.id.slice(0, 8).toUpperCase()}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-900">
                      {t.aiAssistant?.escalate?.categories?.[ticket.category] || ticket.category}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-sm font-medium ${PRIORITY_COLORS[ticket.priority]}`}>
                      {t.aiAssistant?.escalate?.priorities?.[ticket.priority] || ticket.priority}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded-full ${STATUS_COLORS[ticket.status]}`}>
                      {t.aiAssistant?.tickets?.statuses?.[ticket.status] || ticket.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {formatDate(ticket.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setSelectedTicket(ticket)}
                        className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                        title={'View'}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {ticket.status !== 'RESOLVED' && ticket.status !== 'CLOSED' && (
                        <button
                          onClick={() => {
                            setSelectedTicket(ticket);
                            setResolution('');
                          }}
                          className="p-2 text-green-500 hover:bg-green-50 rounded-lg"
                          title={'Resolve'}
                        >
                          <CheckCircle className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 工单详情/解决弹窗 */}
      {selectedTicket && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setSelectedTicket(null)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-lg bg-white rounded-xl shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">
                Ticket #{selectedTicket.id.slice(0, 8).toUpperCase()}
              </h3>
              <button
                onClick={() => setSelectedTicket(null)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                ×
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">{'Category'}</p>
                  <p className="font-medium">{selectedTicket.category}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{'Priority'}</p>
                  <p className={`font-medium ${PRIORITY_COLORS[selectedTicket.priority]}`}>
                    {selectedTicket.priority}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{'Status'}</p>
                  <span className={`px-2 py-1 text-xs rounded-full ${STATUS_COLORS[selectedTicket.status]}`}>
                    {selectedTicket.status}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{'Created'}</p>
                  <p className="text-sm">{formatDate(selectedTicket.createdAt)}</p>
                </div>
              </div>

              {selectedTicket.description && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">Description</p>
                  <p className="text-sm bg-gray-50 p-3 rounded-lg">{selectedTicket.description}</p>
                </div>
              )}

              {selectedTicket.resolution && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">{'Resolution'}</p>
                  <p className="text-sm bg-green-50 p-3 rounded-lg text-green-800">{selectedTicket.resolution}</p>
                </div>
              )}

              {selectedTicket.status !== 'RESOLVED' && selectedTicket.status !== 'CLOSED' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {'Resolution'}
                  </label>
                  <textarea
                    value={resolution}
                    onChange={(e) => setResolution(e.target.value)}
                    rows={4}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                    placeholder={'Enter resolution...'}
                  />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
              <button
                onClick={() => setSelectedTicket(null)}
                className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-100"
              >
                {'Close'}
              </button>
              {selectedTicket.status !== 'RESOLVED' && selectedTicket.status !== 'CLOSED' && (
                <button
                  onClick={handleResolve}
                  disabled={isResolving || !resolution.trim()}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isResolving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {'Resolve'}
                </button>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
